import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { interactionsApi } from '@/lib/api';
import { Building2, Plus, Search, User } from 'lucide-react';
import {
  ACTIVITY,
  Button,
  Card,
  Combobox,
  EmptyState,
  Icon,
  INTERAKTION,
  Input,
  Pagination,
  Spinner,
  TONE_PILL,
  cn,
  formatDatum,
  interaktionInfo,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { InteractionSheet } from './components/InteractionSheet';
import type { Interaction, InteractionType } from '@/types';

/** Einträge je Seite. Ausdrücklich gesendet: Der Standard von GET /interactions ist 20, nicht 25. */
const PER_PAGE = 25;

// Filter nach den Typen, die das Backend kennt (InteractionType); Text aus INTERAKTION.
const TYPEN: InteractionType[] = ['call', 'email', 'meeting', 'note', 'task', 'quote', 'invoice', 'site_visit', 'other'];
const typOptionen = TYPEN.map((value) => ({ value, label: INTERAKTION[value]?.label ?? value }));

export function InteractionsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<InteractionType | ''>('');
  const [page, setPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['interactions', { page, per_page: PER_PAGE, search, type: typeFilter || undefined }],
    queryFn: () => interactionsApi.list({ page, per_page: PER_PAGE, search: search || undefined, type: typeFilter || undefined }),
  });

  return (
    <div>
      <CrmPageHeader
        title="Interaktionen"
        subtitle={`${data?.total ?? 0} Interaktionen insgesamt`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setIsFormOpen(true)}>
            Interaktion anlegen
          </Button>
        }
      >
        <div className="flex w-full flex-wrap items-center gap-3 pb-4">
          <Input
            icon={Search}
            placeholder="Suchen"
            aria-label="Interaktionen suchen"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="min-w-[200px] flex-1"
          />
          <Combobox
            className="w-48"
            aria-label="Alle Typen"
            value={typeFilter}
            onChange={(v) => { setTypeFilter(v as InteractionType | ''); setPage(1); }}
            options={typOptionen}
            placeholder="Alle Typen"
          />
        </div>
      </CrmPageHeader>

      {/* Interactions List */}
      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 p-8 text-muted-foreground">
            <Spinner /> Lädt
          </div>
        ) : data?.items.length === 0 ? (
          <EmptyState title="Keine Interaktionen gefunden" />
        ) : (
          <div className="divide-y divide-border">
            {data?.items.map((interaction: Interaction) => {
              const info = interaktionInfo(interaction.type);
              const art = ACTIVITY[info.type];
              return (
                <div
                  key={interaction.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/interactions/${interaction.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/interactions/${interaction.id}`)}
                  className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-accent"
                >
                  <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', TONE_PILL[art.tone])}>
                    <Icon icon={art.icon} size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="truncate t-body-strong">{interaction.subject}</h3>
                      <span className="shrink-0 t-small text-muted-foreground">{info.label}</span>
                    </div>
                    {interaction.description && (
                      <p className="mt-0.5 line-clamp-2 t-small text-muted-foreground">{interaction.description}</p>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 t-small text-muted-foreground">
                      <span className="t-mono">{formatDatum(interaction.occurred_at)}</span>
                      {(interaction.customer_name || interaction.partner_name) && (
                        <span className="flex items-center gap-1">
                          <Building2 size={13} strokeWidth={1.75} aria-hidden />
                          {interaction.customer_name || interaction.partner_name}
                        </span>
                      )}
                      {interaction.contact_name && (
                        <span className="flex items-center gap-1">
                          <User size={13} strokeWidth={1.75} aria-hidden />
                          {interaction.contact_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {data && data.total > 0 && (
        <Pagination page={page} pageCount={data.pages} onPageChange={setPage} />
      )}

      <InteractionSheet open={isFormOpen} onOpenChange={setIsFormOpen} title="Interaktion anlegen" showEntitySelector />
    </div>
  );
}
